'use client';

import React from 'react';
import { IoPlaySharp } from 'react-icons/io5';

import type { Media as MediaType } from '../../../payload/payload-types';
import { ProductType } from '../../../payload/payload-types';
import { MatchingProductsProps } from '@/app/_blocks/MatchingProductsGraphQL/types';
import { Button, ButtonAppearance } from '../Button';
import { Media } from '../Media';
import { captureComponentError } from '../ServerErrorBoundary';

import classes from './MatchingProducts.module.scss';
import ResimdoIcon from '../ResimdoIcon';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { Gutter } from '../Gutter';
import { renderTrademarkText } from '../TrademarkText';

type MatchingProductType = MatchingProductsProps['products'][0];

const MatchingProduct: React.FC<{ product: MatchingProductType; mediaOnly: boolean }> = ({
  product,
  mediaOnly,
}) => {
  const productType = product.prodType.productType as ProductType;
  const imageId = product.prodType.image;
  const videoId = product.prodType.video;
  const btnItem = product.prodType.btn;
  const availableImages = productType.images as Array<{ id: string; image: MediaType }>;
  const imageItem = availableImages?.find(img => img.id === imageId);
  const image: MediaType | undefined = imageItem?.image;
  const features =
    product.prodType.features
      ?.map(feature => productType.features?.find(f => f.id === feature.feature))
      .filter(Boolean) || [];

  // Handle missing image case
  const hasImage = !!image;

  if (!hasImage) {
    const error = new Error(
      `No image found for imageId: ${imageId} in productType: ${productType?.name}`,
    );
    captureComponentError(error, 'MatchingProduct', {
      imageId,
      productType: productType?.name,
      availableImages: availableImages?.map(img => img.id),
      product: product.id,
    });
  }

  return (
    <div>
      {hasImage ? (
        <div className={`${classes.productImage} ${mediaOnly ? classes.mediaOnlyImage : ''}`}>
          <Media
            resource={image}
            alt={image.alt}
            className={`${classes.productImage} ${mediaOnly ? classes.mediaOnlyImage : ''}`}
            imageSize="square"
          />
          <div className={classes.productLabel}>
            <span>{productType?.name ? renderTrademarkText(productType.name) : null}</span>
          </div>
          {videoId && (
            <button data-video={videoId} title="Play Video">
              <IoPlaySharp />
            </button>
          )}
        </div>
      ) : (
        <div className={classes.productImageContainer}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f0f0f0',
              minHeight: '200px',
              borderRadius: 'var(--border-radius)',
              color: '#666',
            }}
          >
            <p>Image not found (ID: {imageId})</p>
          </div>
          <div className={classes.productLabel}>
            <span>{productType?.name ? renderTrademarkText(productType.name) : 'Unknown Product'}</span>
          </div>
        </div>
      )}

      {!mediaOnly && (
        <div className={classes.productContent}>
          {hasImage ? (
            <>
              {product.prodType.customDescription ? (
                <div>{renderTrademarkText(product.prodType.customDescription)}</div>
              ) : (
                <div>{productType?.description ? renderTrademarkText(productType.description) : null}</div>
              )}
              <div className={classes.features}>
                {features.map((feature, featureIndex) => (
                  <div key={feature?.id || `feature-${featureIndex}`} className={classes.feature}>
                    {feature ? (
                      <>
                        <span className={classes.featureIcon}>
                          <ResimdoIcon icon={feature.icon} size="SM" mobileSize="XXS" />
                        </span>
                        <span>{renderTrademarkText(feature.label)}</span>
                        {feature.featureVideoId && (
                          <button data-video={feature.featureVideoId} title="Play Video">
                            <IoPlaySharp />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <span className={classes.featureIcon}>
                          {/* Empty placeholder to maintain layout */}
                        </span>
                        <span>Feature not available</span>
                        <span></span>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className={classes.buttonContainer}>
                {btnItem && btnItem?.hideButton !== true ? (
                  <Button
                    type={btnItem.type || 'reference'}
                    appearance="accent"
                    href={btnItem.type === 'custom' ? btnItem?.url : undefined}
                    reference={btnItem.type === 'reference' ? btnItem?.reference : undefined}
                    document={
                      btnItem.type === 'download' && btnItem.document && typeof btnItem.document === 'object'
                        ? btnItem.document
                        : undefined
                    }
                    label={btnItem.label}
                    newTab={btnItem.newTab}
                    className={classes.button}
                  />
                ) : (
                  <div className={classes.buttonPlaceholder}></div>
                )}
              </div>
            </>
          ) : (
            <div>{productType?.description ? renderTrademarkText(productType.description) : 'No description available'}</div>
          )}
        </div>
      )}
    </div>
  );
};

export const MatchingProducts: React.FC<{
  className?: string;
  matchingProducts: MatchingProductsProps;
}> = ({ className, matchingProducts }) => {
  const [supportsScrollMarkers, setSupportsScrollMarkers] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const singleProductClass = matchingProducts.products.length === 1 ? classes.singleProduct : '';

  // Feature detection for scroll markers - run once on mount
  React.useEffect(() => {
    const hasScrollMarkerSupport =
      typeof CSS !== 'undefined' && CSS.supports && CSS.supports('scroll-marker-group', 'after');
    setSupportsScrollMarkers(hasScrollMarkerSupport);
  }, []);

  // Set up scroll tracking - always run, but only attach listeners when needed
  React.useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container || supportsScrollMarkers) {
      return;
    }

    let ticking = false;

    const updateActiveIndex = () => {
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      itemRefs.current.forEach((item, index) => {
        if (!item) return;

        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(itemCenter - containerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActiveIndex);
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    updateActiveIndex();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [supportsScrollMarkers]);

  // Handle manual dot clicks
  const handleDotClick = React.useCallback((index: number) => {
    const container = scrollContainerRef.current;
    const item = itemRefs.current[index];

    if (!container || !item) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const scrollLeft = container.scrollLeft + itemRect.left - containerRect.left;

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, []);

  // Show indicators only when we have multiple products and no native scroll marker support
  const showManualIndicators = !supportsScrollMarkers && matchingProducts.products.length > 1;

  return (
    <Gutter className={classes.wrapper}>
      {(() => {
        const headlineText =
          matchingProducts.productsHeadline?.text || matchingProducts.headline_legacy;
        const headlineTag = matchingProducts.productsHeadline?.tag || 'h2';
        const HeadlineTag = headlineTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        return headlineText ? <HeadlineTag>{renderTrademarkText(headlineText)}</HeadlineTag> : null;
      })()}
      <div
        className={[className, classes.productsWrapper, singleProductClass]
          .filter(Boolean)
          .join(' ')}
        ref={scrollContainerRef}
      >
        {matchingProducts.products.map((product, index) => (
          <div
            key={product.id}
            className={classes.product}
            ref={el => { itemRefs.current[index] = el; }}
          >
            <MatchingProduct product={product} mediaOnly={matchingProducts.mediaOnly} />
          </div>
        ))}
      </div>
      {showManualIndicators && (
        <div className={classes.manualScrollMarkers}>
          {matchingProducts.products.map((_, index) => (
            <button
              key={index}
              className={`${classes.manualScrollMarker} ${
                index === activeIndex ? classes.active : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to product ${index + 1}`}
            />
          ))}
        </div>
      )}
      <DebugJsonBlock block={{ ...matchingProducts }} />
    </Gutter>
  );
};

export default MatchingProducts;
